import type { Rule } from "../../services/rule.tree.service";
import { INGESTION_FIELD_TYPE_ID } from "./data.ingestion.const";
import type { IngestionFieldDefinition } from "./data.ingestion.service";
import { Rules } from "../s25-rule-tree/s25.rule.const";
import { S25RuleTreeUtil } from "../s25-rule-tree/s25.rule.tree.util";

function isConditionGroup(node: Rules.Conditions | Rules.Condition): node is Rules.Conditions {
    return "children" in node && Array.isArray(node.children);
}

function resolveFieldLabel(
    sourceItem: Rules.SourceItem | null | undefined,
    fieldDefinitions: IngestionFieldDefinition[],
): string {
    if (sourceItem?.itemName?.trim()) {
        return sourceItem.itemName.trim();
    }
    const fieldDef = fieldDefinitions.find((field) => field.fieldDefinitionId === sourceItem?.itemId);
    return fieldDef?.fieldLabel?.trim() || "Field";
}

function formatPickerConditionValue(value: Record<string, unknown>): string {
    const itemName = value.itemName;
    if (typeof itemName === "string" && itemName.trim()) {
        return itemName.trim();
    }
    const itemId = value.itemId;
    if (itemId != null && String(itemId).trim()) {
        return String(itemId);
    }
    const rawValue = value.value;
    if (rawValue != null && String(rawValue).trim()) {
        return String(rawValue).trim();
    }
    return "";
}

function formatConditionValue(
    value: Rules.ConditionValue | null | undefined,
    condition: Rules.Condition,
    fieldDefinitions: IngestionFieldDefinition[],
): string {
    if (value == null) {
        return "";
    }
    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (typeof value === "number") {
        return String(value);
    }
    if (typeof value === "object" && !Array.isArray(value)) {
        const pickerLabel = formatPickerConditionValue(value as Record<string, unknown>);
        if (pickerLabel) {
            return pickerLabel;
        }
    }
    const stringValue = String(value);
    if (condition.type === INGESTION_FIELD_TYPE_ID && condition.sourceItem?.itemId) {
        const sourceField = fieldDefinitions.find((field) => field.fieldDefinitionId === condition.sourceItem?.itemId);
        const matchedOption = sourceField?.options?.find((fieldOption) => fieldOption.option === stringValue);
        if (matchedOption?.option) {
            return matchedOption.option;
        }
    }
    return stringValue;
}

function formatConditionLeaf(condition: Rules.Condition, fieldDefinitions: IngestionFieldDefinition[]): string {
    const fieldName = resolveFieldLabel(condition.sourceItem, fieldDefinitions);
    if (Rules.valuelessOperators.has(condition.operator)) {
        return `${fieldName} ${condition.operator}`;
    }
    const values = (condition.values ?? [])
        .map((value) => formatConditionValue(value, condition, fieldDefinitions))
        .filter((value) => value.length > 0);
    if (!values.length) {
        return `${fieldName} ${condition.operator}`;
    }
    return `${fieldName} ${condition.operator} ${values.join(", ")}`;
}

export function formatIngestionRuleConditions(
    conditions: Rules.Conditions,
    fieldDefinitions: IngestionFieldDefinition[],
): string {
    const parts = conditions.children
        .map((child) => {
            if (isConditionGroup(child)) {
                const nested = formatIngestionRuleConditions(child, fieldDefinitions);
                return nested ? `(${nested})` : "";
            }
            return formatConditionLeaf(child, fieldDefinitions);
        })
        .filter((part) => part.length > 0);

    if (!parts.length) {
        return "";
    }
    const joined = parts.join(` ${conditions.operator} `);
    return parts.length > 1 ? `(${joined})` : joined;
}

export function getHiddenFieldShowHints(
    fieldDefinitionId: number | undefined,
    rules: Rule[],
    fieldDefinitions: IngestionFieldDefinition[],
): string[] {
    if (!fieldDefinitionId) {
        return ["Hidden"];
    }

    const hints: string[] = [];
    for (const rule of rules) {
        if (!rule.active) {
            continue;
        }
        let parsed: Rules.Rule;
        try {
            parsed = S25RuleTreeUtil.parseRule(rule);
        } catch {
            continue;
        }

        const conditionText = formatIngestionRuleConditions(parsed.conditions, fieldDefinitions);
        const showTargets = parsed.targets.showField ?? [];
        if (showTargets.some((target) => target.itemId === fieldDefinitionId)) {
            hints.push(conditionText ? `Shown when ${conditionText}` : "Shown when rule conditions are met");
        }

        const hideTargets = parsed.targets.hideField ?? [];
        if (hideTargets.some((target) => target.itemId === fieldDefinitionId)) {
            hints.push(conditionText ? `Hidden when ${conditionText}` : "Hidden when rule conditions are met");
        }
    }

    if (!hints.length) {
        const fieldDef = fieldDefinitions.find((field) => field.fieldDefinitionId === fieldDefinitionId);
        if (fieldDef?.hidden) {
            return ["Hidden by default"];
        }
    }

    return hints;
}
